import {
  ScmAuth,
  scmAuthApiRef,
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import {
  analyticsApiRef,
  AnyApiFactory,
  ApiRef,
  BackstageIdentityApi,
  configApiRef,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  OAuthApi,
  oauthRequestApiRef,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import { GithubAuth } from '@backstage/core-app-api';
import { GoogleAnalytics4 } from '@backstage-community/plugin-analytics-module-ga4';
import {
  AUTH_TYPE_AZURE_AD,
  DEFAULT_GITHUB_ENV,
  DEFAULT_SCM_HOST,
} from './custom/util/constants';
import { DevHubOAuth } from './custom/auth/devHubOauth';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { CatalogClient } from '@backstage/catalog-client';
import {
  ApolloClientQueryProvider,
  CatalogServiceFactory,
  ConfigHelper,
  FusionCatalogService,
} from '@devxdiscover/devhub-common';
import { detPortalApiRef, DetPortalApi } from '@devxdiscover/plugin-det-portal';
import { JwtHelper } from './custom/util/jwtUtil';
import { encrypt } from './custom/util/encrypt';

/**
 * Custom GitHub Authentication API for GitHub Enterprise Cloud supporting custom scopes.
 */
export const devHubGhecAuthApiRef: ApiRef<
  OAuthApi & ProfileInfoApi & SessionApi
> = createApiRef({
  id: 'devhub.auth.ghec',
});

/**
 * Custom OIDC Authentication API for Azure AD supporting custom scopes.
 */
export const devhubAuthApiRef: ApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
> = createApiRef({
  id: `devhub.auth`,
});

/**
 * A set of APIs that are available to all Backstage plugins.
 */
export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: catalogApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      CatalogServiceFactory.create(
        new CatalogClient({ discoveryApi, fetchApi }),
        () =>
          new FusionCatalogService(
            new ApolloClientQueryProvider(
              ConfigHelper.instance.apiUrl('fusion/query').href,
            ),
            'lru',
          ),
      ),
  }),

  createApiFactory({
    api: devHubGhecAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi }) =>
      GithubAuth.create({
        provider: {
          id: 'github',
          icon: () => null,
          title: 'GitHub',
        },
        discoveryApi,
        oauthRequestApi,
        defaultScopes: ['read:user', 'repo', 'repo:write', 'read:org', 'gist'],
        environment: DEFAULT_GITHUB_ENV,
      }),
  }),

  // Azure AD Auth API with custom scopes.
  createApiFactory({
    api: devhubAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },

    factory: ({ discoveryApi, oauthRequestApi, configApi }) => {
      ConfigHelper.init(configApi);
      const defaultAuthProvider =
        ConfigHelper.instance.config.getOptionalString('app.authBackend') ??
        AUTH_TYPE_AZURE_AD;
      return new DevHubOAuth({
        discoveryApi,
        oauthRequestApi,
        provider: {
          id: `devhub-${defaultAuthProvider}-auth`,
          title: 'DevHub SSO',
          icon: () => null,
        },
        environment: configApi.getOptionalString('auth.environment'),
      });
    },
  }),

  // Access to the SCM integrations API.
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),

  // Support for GitHub OAuth authentication in the scaffolder
  createApiFactory({
    api: scmAuthApiRef,
    deps: {
      customGhecAuthApi: devHubGhecAuthApiRef,
    },
    factory: ({ customGhecAuthApi }) => {
      return ScmAuth.merge(
        ScmAuth.forGithub(customGhecAuthApi, {
          host: DEFAULT_SCM_HOST,
        }),
      );
    },
  }),

  // Support for Google Analytics 4
  createApiFactory({
    api: analyticsApiRef,
    deps: {
      configApi: configApiRef,
      identityApi: identityApiRef,
      devhubAuthApi: devhubAuthApiRef,
    },
    factory: ({ configApi, identityApi, devhubAuthApi }) => {
      return GoogleAnalytics4.fromConfig(configApi, {
        identityApi,
        userIdTransform: async _userEntityRef => {
          const token = await devhubAuthApi.getAccessToken();
          const extracted = JwtHelper.unsafeDecodePayload<{ oid: string }>(
            token,
          );
          return extracted?.oid ? encrypt(extracted.oid) : '';
        }, // Required to be able to capture user identity.
      });
    },
  }),

  // DET Portal API
  createApiFactory({
    api: detPortalApiRef,
    deps: { discoveryApi: discoveryApiRef },
    factory: ({ discoveryApi }) => new DetPortalApi(discoveryApi),
  }),
];
